import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Divider,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  ListSubheader
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { splitBySize, oversizeMessage } from '../../utils/fileSizeLimit';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { filesFromEvent } from '../../utils/dropPasteFiles';
import { isPrimaryDocType, splitDocTypes, isOrganizationDocType } from '../../utils/docTypeGroups';

// Font size constants — same scale as the typing-center Create Work Order page.
const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

// Section titles inside cards.
const SECTION_TITLE_SX = { fontSize: fontSize.md, fontWeight: 600, color: 'primary.main' };
// Every field on the page at 13px.
const PAGE_SX = {
  '& .MuiInputBase-root': { fontSize: fontSize.sm },
  '& .MuiInputLabel-root': { fontSize: fontSize.sm },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
  '& .MuiButton-root': { textTransform: 'none', fontSize: fontSize.sm },
};
const CARD_SX = { mb: 2 };
const CONTENT_SX = { p: 2, '&:last-child': { pb: 2 } };

// Create initial customer object
const createCustomer = (index = 0) => ({
  id: Date.now() + index,
  name: '',
  passportNumber: '',
  emiratesId: '',
  // Optional contact details — sent to Thaklis with the employee, not stored in Managers.
  phone: '',
  email: '',
  services: [],   // selected service ids for THIS applicant
  // Custom field values for THIS applicant, keyed by fieldId (shared across the applicant's
  // selected services; distributed to each service's line item on save): { [fieldId]: value }
  fieldValues: {},
  documents: []
});

// Merge the custom fields of an applicant's selected services into one deduped list (by fieldId).
// Required if ANY selected service that carries the field marks it required.
const mergeCustomFieldsFor = (customer, allServices) => {
  const map = new Map();
  (customer?.services || []).forEach(sid => {
    const svc = allServices.find(s => s.id === sid);
    (svc?.customFields || []).forEach(f => {
      const key = f.fieldId || f.code;
      if (!key) return;
      const ex = map.get(key);
      if (ex) { if (f.required) ex.required = true; }
      else map.set(key, { ...f, fieldId: f.fieldId || f.code, required: !!f.required });
    });
  });
  return Array.from(map.values());
};

// Merge the required-document rules of an applicant's selected services (deduped by code).
const mergeRequiredDocsFor = (customer, allServices) => {
  const map = new Map();
  (customer?.services || []).forEach(sid => {
    const svc = allServices.find(s => s.id === sid);
    (svc?.documentRequirements || []).forEach(r => {
      if (!r.code) return;
      const ex = map.get(r.code);
      if (ex) { if (r.required) ex.required = true; }
      else map.set(r.code, { ...r, required: !!r.required });
    });
  });
  return Array.from(map.values());
};

const PortalCreateWorkOrder = ({ portalUser }) => {
  const navigate = useNavigate();
  // Employee/document tracking is a client-only feature — a typing-center customer of a B2B provider
  // gets none, even when that B2B has Thaklis enabled. Prefer the server flag; fall back to the
  // Thaklis-features flag for sessions stored before employeeTrackingEnabled existed.
  const employeeTrackingActive = (() => {
    try {
      const org = JSON.parse(localStorage.getItem('portalOrganization') || '{}');
      return org?.employeeTrackingEnabled !== undefined
        ? org.employeeTrackingEnabled === true
        : org?.thaklisEnabled !== false;
    } catch (e) {
      return false;
    }
  })();
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingDocTypes, setLoadingDocTypes] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [services, setServices] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);

  // Form state - Common contact info
  // Prefill the common contact from the portal user's own organization (email + contact number),
  // mirroring how the TC create page fills contact from the selected organization.
  const [commonEmail, setCommonEmail] = useState(() => {
    try { return JSON.parse(localStorage.getItem('portalOrganization') || '{}')?.email || ''; } catch (e) { return ''; }
  });
  const [commonPhone, setCommonPhone] = useState(() => {
    try {
      const o = JSON.parse(localStorage.getItem('portalOrganization') || '{}');
      return o?.contactNumber || o?.phone || '';
    } catch (e) { return ''; }
  });
  const [notes, setNotes] = useState('');
  // Optional short label shown in the work order list and detail (max 50).
  const [title, setTitle] = useState('');

  // Tab-based customers (each customer has their own documents)
  const [customers, setCustomers] = useState([createCustomer(0)]);
  const [activeTab, setActiveTab] = useState(0);

  // Queue of uploaded docs whose OCR-detected name differs from the customer already on
  // that tab. Each entry drives a "detected applicant is different" prompt (head of queue).
  const [pendingMismatches, setPendingMismatches] = useState([]);

  // Document upload state
  const [selectedDocType, setSelectedDocType] = useState('');

  // Services are selected per applicant (each customer carries its own `services` id list).
  // Confirm dialog when some applicants have no unique identifier
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Summary sidebar expanded state
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // A B2B's portal offers only internal services (with resolved prices), so show prices at creation;
  // a normal TC hides them because pricing is confirmed by the TC after review.
  let isB2b = false;
  try { isB2b = JSON.parse(localStorage.getItem('portalOrganization') || '{}')?.isB2b === true; } catch (e) { isB2b = false; }
  const svcTotal = (svc) => Number(svc.service_charge || 0) + Number(svc.govt_charge || 0) + Number(svc.tax_on_service_charge || 0);

  useEffect(() => {
    fetchServices();
    fetchDocumentTypes();
  }, []);

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      // Show Thaklis services only when the parent TC has Thaklis features enabled AND offers Thaklis
      // services on work orders (Work Order Settings, default off). Otherwise internal services only.
      let hideThaklis = true;
      try {
        const org = JSON.parse(localStorage.getItem('portalOrganization') || '{}');
        hideThaklis = org?.thaklisEnabled === false || org?.thaklisServicesEnabled !== true;
      } catch (e) { hideThaklis = true; }

      // Load Thaklis + internal services in parallel (Thaklis skipped when hidden).
      const [thaklisRes, internalRes] = await Promise.all([
        hideThaklis ? Promise.resolve({ data: { data: [] } })
          : ApiService.getPortalServices().catch(() => ({ data: { data: [] } })),
        ApiService.getPortalInternalServices().catch(() => ({ data: { data: [] } }))
      ]);

      const thaklis = (thaklisRes?.data?.data || []).map(s => ({
        id: s.id,
        name: s.name || s.service_name,
        description: s.description || '',
        service_charge: s.serviceCharge || s.service_charge || 0,
        tax_on_service_charge: s.taxOnServiceCharge || s.tax_on_service_charge || 0,
        govt_charge: s.govtCharge || s.govt_charge || 0,
        type: 'THAKLIS'
      }));
      const internal = (internalRes?.data?.data || []).map(s => ({
        id: s.id,
        name: s.name,
        description: s.description || '',
        service_charge: s.service_charge || s.serviceCharge || 0,
        tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
        govt_charge: s.govt_charge || s.govtCharge || 0,
        type: 'INTERNAL',
        // Custom fields (value pre-filled from the org default) and required documents for this service.
        customFields: (s.customFields || s.custom_fields || []).map(f => ({
          fieldId: f.fieldId || f.tcCustomFieldId || f.id,
          code: f.code,
          label: f.label,
          fieldType: f.fieldType || f.field_type || 'TEXT',
          options: f.options || [],
          required: f.required === true,
          orgDefault: f.orgDefault ?? f.value ?? ''
        })),
        documentRequirements: (s.documentRequirements || s.document_requirements || []).map(r => ({
          code: r.code,
          label: r.label,
          required: r.required !== false
        }))
      }));

      setServices([...thaklis, ...internal]);
    } catch (err) {
      console.error('Failed to fetch services:', err);
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchDocumentTypes = async () => {
    setLoadingDocTypes(true);
    try {
      // Use the portal's own typing center document types (falls back to the Thaklis
      // standard list server-side when the TC hasn't defined any).
      const response = await ApiService.getPortalTcDocumentTypes();
      const types = (response.data?.data || []).map(dt => ({
        code: dt.code, label: dt.label, category: dt.category, holderType: dt.holder_type || dt.holderType, isPrimary: isPrimaryDocType(dt)
      }));
      setDocumentTypes(types);
    } catch (err) {
      console.error('Failed to fetch document types:', err);
      setDocumentTypes([]);
    } finally {
      setLoadingDocTypes(false);
    }
  };

  // Tab management
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedDocType('');
  };

  const handleAddCustomer = () => {
    const newCustomer = createCustomer(customers.length);
    setCustomers(prev => [...prev, newCustomer]);
    setActiveTab(customers.length);
  };

  const handleRemoveCustomer = (customerId) => {
    if (customers.length === 1) {
      setError('At least one customer is required');
      return;
    }
    const customerIndex = customers.findIndex(c => c.id === customerId);
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    if (activeTab >= customers.length - 1) {
      setActiveTab(Math.max(0, customers.length - 2));
    } else if (activeTab > customerIndex) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleCustomerChange = (customerId, field, value) => {
    setCustomers(prev => prev.map(c =>
      c.id === customerId ? { ...c, [field]: value } : c
    ));
  };

  // Service management (global)
  // Toggle a service on the ACTIVE applicant only (per-customer selection).
  const handleServiceToggle = (serviceId) => {
    setCustomers(prev => prev.map((c, i) => {
      if (i !== activeTab) return c;
      if (c.services.includes(serviceId)) {
        // Deselect: drop the service. Shared field values stay (harmless — only fields belonging to
        // a still-selected service are shown and distributed on save).
        return { ...c, services: c.services.filter(id => id !== serviceId) };
      }
      // Select: seed the applicant's shared field values from this service's org defaults, without
      // overwriting anything already filled (a shared field keeps its value across services).
      const svc = services.find(s => s.id === serviceId);
      const nextFieldValues = { ...(c.fieldValues || {}) };
      (svc?.customFields || []).forEach(f => {
        const def = f.orgDefault;
        const cur = nextFieldValues[f.fieldId];
        if (def != null && String(def).trim() !== '' && (cur == null || String(cur).trim() === '')) {
          nextFieldValues[f.fieldId] = def;
        }
      });
      return { ...c, services: [...c.services, serviceId], fieldValues: nextFieldValues };
    }));
  };

  // Update one shared custom field value (keyed by fieldId) on the active applicant.
  const handleFieldValueChange = (fieldId, value) => {
    setCustomers(prev => prev.map((c, i) => i === activeTab
      ? { ...c, fieldValues: { ...(c.fieldValues || {}), [fieldId]: value } }
      : c));
  };

  // Document management (per customer) — supports selecting multiple files at once. Each file is
  // staged as its own document (same type) and OCR-extracted individually; on submit, files of the
  // same document type are attached together in one bulk batch.
  // `forced` ({ code, label }) is passed by a required-document slot so the upload targets that
  // specific doc type; without it the upload uses the "add another document" type dropdown.
  const handleFileUpload = async (event, forced = null) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length === 0) return;
    const { ok: files, tooBig } = splitBySize(picked);
    if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
    if (files.length === 0) return;

    const useDocType = forced?.code || selectedDocType;
    if (!useDocType) {
      setError('Please select a document type');
      return;
    }

    const docType = documentTypes.find(d => (d.code || d.id) === useDocType);
    const currentCustomer = customers[activeTab];
    const baseId = Date.now();

    const stagedDocs = files.map((file, i) => ({
      id: baseId + i,
      file,
      fileName: file.name,
      fileSize: file.size,
      documentType: useDocType,
      documentTypeName: forced?.label || docType?.label || docType?.name || useDocType,
      extracting: true,
      // Extracted tracking metadata (filled after OCR)
      documentNumber: null,
      issueDate: null,
      expiryDate: null
    }));

    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: [...c.documents, ...stagedDocs] }
        : c
    ));

    if (!forced) setSelectedDocType('');
    if (event.target) event.target.value = '';

    const clean = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).trim();
      return (!s || s.toLowerCase() === 'null') ? '' : s;
    };

    // OCR extraction per file: auto-fill the applicant's identity/contact when present (only into
    // empty fields) and stash each document's own number/issue/expiry.
    await Promise.all(stagedDocs.map(async (staged) => {
      try {
        const response = await ApiService.extractPortalDocument(staged.file, 'GENERAL', staged.documentType);
        const extracted = response?.data?.data || {};
        // Surface a clear reason when extraction didn't run (e.g. the TC's OpenAI key is not
        // configured, or it was skipped) instead of silently filling nothing. The document is still
        // staged either way — extraction is best-effort.
        const extractFailure = response?.data?.success === false
          ? (response.data.error || response.data.message)
          : (extracted._extractionSkipped
            ? (extracted._extractionSkippedReason || 'Automatic field extraction was skipped — enter the fields manually.')
            : null);
        if (extractFailure) {
          setError(extractFailure);
        }
        const fullName = clean(extracted.fullName);
        const passportNumber = clean(extracted.passportNumber);
        const emiratesIdNumber = clean(extracted.emiratesIdNumber);
        const phoneNumber = clean(extracted.phoneNumber);
        const email = clean(extracted.email);
        const documentNumber = resolveExtractedDocNumber(extracted, staged.documentType);
        const issueDate = clean(extracted.issueDate);
        const expiryDate = clean(extracted.expiryDate);

        let mismatch = null;
        setCustomers(prev => prev.map(c => {
          if (c.id !== currentCustomer.id) return c;
          const existingName = c.name?.trim() || '';
          // A name was detected that differs from the customer already on this tab —
          // don't touch the identity; queue a prompt asking whether to split it out.
          if (existingName && fullName && existingName.toLowerCase() !== fullName.toLowerCase()) {
            mismatch = {
              docId: staged.id,
              sourceCustomerId: c.id,
              existingName,
              extracted: {
                fullName,
                passportNumber,
                emiratesId: emiratesIdNumber,
                phone: phoneNumber,
                email
              }
            };
            // Clear the spinner + record this doc's own metadata; leave identity as-is.
            return {
              ...c,
              documents: c.documents.map(d => d.id === staged.id
                ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
                : d)
            };
          }
          return {
            ...c,
            // Only fill fields that are currently empty (never overwrite user input)
            name: existingName ? c.name : (fullName || c.name),
            passportNumber: c.passportNumber?.trim() ? c.passportNumber : (passportNumber || c.passportNumber),
            emiratesId: c.emiratesId?.trim() ? c.emiratesId : (emiratesIdNumber || c.emiratesId),
            phone: c.phone?.trim() ? c.phone : (phoneNumber || c.phone),
            email: c.email?.trim() ? c.email : (email || c.email),
            documents: c.documents.map(d => d.id === staged.id
              ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
              : d)
          };
        }));
        if (mismatch) {
          setPendingMismatches(prev =>
            prev.some(m => m.docId === mismatch.docId) ? prev : [...prev, mismatch]);
        }
      } catch (extractErr) {
        console.error('Document extraction failed:', extractErr);
        setError('Document extraction failed — enter the fields manually.');
        // Extraction is best-effort — clear the spinner and keep the staged file.
        setCustomers(prev => prev.map(c =>
          c.id === currentCustomer.id
            ? { ...c, documents: c.documents.map(d => d.id === staged.id ? { ...d, extracting: false } : d) }
            : c
        ));
      }
    }));
  };

  // Stage documents dragged in or pasted (Ctrl/Cmd+V) through the same staging + OCR path.
  const handleAddDroppedFiles = async (e, forced = null) => {
    const files = filesFromEvent(e);
    if (!files.length) return;
    e.preventDefault();
    if (!forced && !selectedDocType) {
      setError('Please select a document type');
      return;
    }
    await handleFileUpload({ target: { files, value: '' } }, forced);
  };

  const handleRemoveDocument = (docId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: c.documents.filter(d => d.id !== docId) }
        : c
    ));
  };

  // Name-mismatch prompt — user chose to keep the doc with the existing customer.
  const handleKeepSameCustomer = () => {
    setPendingMismatches(prev => prev.slice(1));
  };

  // Name-mismatch prompt — user chose to split the detected applicant into a new
  // customer: create a tab, fill it with the extracted identity, move the doc there.
  const handleMoveToNewCustomer = () => {
    const mismatch = pendingMismatches[0];
    if (!mismatch) return;

    const newIndex = customers.length;
    const newCustomer = {
      ...createCustomer(newIndex),
      name: mismatch.extracted.fullName || '',
      passportNumber: mismatch.extracted.passportNumber || '',
      emiratesId: mismatch.extracted.emiratesId || '',
      phone: mismatch.extracted.phone || '',
      email: mismatch.extracted.email || ''
    };

    setCustomers(prev => {
      let movedDoc = null;
      const updated = prev.map(c => {
        if (c.id !== mismatch.sourceCustomerId) return c;
        movedDoc = c.documents.find(d => d.id === mismatch.docId) || null;
        return { ...c, documents: c.documents.filter(d => d.id !== mismatch.docId) };
      });
      return [...updated, { ...newCustomer, documents: movedDoc ? [movedDoc] : [] }];
    });

    setActiveTab(newIndex);
    setPendingMismatches(prev => prev.slice(1));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if customer is complete (has name)
  const isCustomerComplete = useCallback((customer) => {
    return customer.name.trim().length > 0 && customer.services.length > 0;
  }, []);

  // Validate the form; the first failure is surfaced as a toast (mirrors the TC create page).
  const validate = () => {
    // Every applicant must have a name — a blank-name customer would otherwise be silently
    // dropped along with its services/documents.
    if (customers.some(c => !c.name.trim())) {
      setError('Please enter a name for every applicant');
      return false;
    }
    if (!customers.some(c => c.services.length > 0)) {
      setError('Select at least one service for an applicant');
      return false;
    }
    // Required custom fields and required documents must be provided (hard block), merged per
    // applicant across their selected services (deduped). Only docs marked required are enforced.
    for (const c of customers) {
      if (!c.name.trim()) continue;
      const uploadedTypes = new Set((c.documents || []).map(d => d.documentType || d.document_type));
      for (const f of mergeCustomFieldsFor(c, services)) {
        if (!f.required) continue;
        const v = c.fieldValues?.[f.fieldId];
        if (v == null || String(v).trim() === '') {
          setError(`Fill required field "${f.label}" for ${c.name}`);
          return false;
        }
      }
      for (const r of mergeRequiredDocsFor(c, services)) {
        if (r.required && !uploadedTypes.has(r.code)) {
          setError(`Upload required document "${r.label}" for ${c.name}`);
          return false;
        }
      }
    }
    return true;
  };

  // An applicant can only be tracked as a Thaklis employee if they have a passport or Emirates ID.
  const customerHasIdentifier = (c) => Boolean(c.passportNumber?.trim() || c.emiratesId?.trim());

  // Create/reuse each applicant's Thaklis employee and attach their documents. The internal org
  // is forced server-side from the portal token, so nothing here can target another org.
  // Per-customer in parallel; applicants without an identifier are skipped entirely.
  const syncEmployeesAndDocuments = async (validCustomers, failedDocIds = new Set()) => {
    // create-or-reuse still runs for any customer with a document + identifier; only the individual
    // document upload to Thaklis is skipped for a document whose WO (/documents) upload failed, so the
    // employee is created/reused but a doc that isn't on the WO is never pushed to Thaklis.
    const isSyncable = (d) => d.file && !failedDocIds.has(d.id) && !isOrganizationDocType(documentTypes, d.documentType);
    const trackable = validCustomers.filter(c =>
      customerHasIdentifier(c) && (c.documents || []).some(isSyncable)
    );
    if (trackable.length === 0) return;

    await Promise.all(trackable.map(async (customer) => {
      try {
        // Create/reuse the employee ONCE per customer before any upload — the uploads below
        // run in parallel, and doing create-or-reuse inside each of them would race and
        // create a duplicate employee per document type.
        const resp = await ApiService.createOrReusePortalEmployee({
          name: customer.name?.trim() || '',
          passport_number: customer.passportNumber?.trim() || null,
          emirates_id: customer.emiratesId?.trim() || null,
          phone: customer.phone?.trim() || null,
          email: customer.email?.trim() || null
        });
        const uid = resp?.data?.data?.uid;
        if (!uid) {
          throw new Error(resp?.data?.error || 'No employee uid returned');
        }

        // Group this customer's staged files by document type — files of the same
        // type are attached together in one bulk batch (all become the latest version).
        const groups = new Map();
        for (const doc of (customer.documents || [])) {
          if (!isSyncable(doc)) continue;
          if (!groups.has(doc.documentType)) groups.set(doc.documentType, []);
          groups.get(doc.documentType).push(doc);
        }
        await Promise.all(Array.from(groups.entries()).map(async ([documentType, docs]) => {
          const files = docs.map(d => d.file);
          // Shared metadata for the batch: first non-empty value across the group's files.
          const pick = (key) => {
            const hit = docs.find(d => d[key]);
            return hit ? hit[key] : null;
          };
          try {
            await ApiService.uploadPortalEmployeeDocuments(uid, files, documentType, {
              documentNumber: pick('documentNumber'),
              issueDate: pick('issueDate'),
              expiryDate: pick('expiryDate')
            });
          } catch (docErr) {
            console.error(`Failed to sync ${documentType} documents for ${customer.name}:`, docErr);
          }
        }));
      } catch (custErr) {
        console.error(`Failed to sync employee for ${customer.name}:`, custErr);
        setError(prev => prev || `Some applicants could not be synced to Thaklis (${customer.name}).`);
      }
    }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Warn before submitting when an applicant can't be tracked — skipped when Thaklis is disabled
    // (no employee tracking at all).
    const anyMissingId = customers.filter(c => c.name.trim()).some(c => !customerHasIdentifier(c));
    if (employeeTrackingActive && anyMissingId && !confirmOpen) {
      setConfirmOpen(true);
      return;
    }
    await proceedSubmit();
  };

  const proceedSubmit = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError(null);

    try {
      const validCustomers = customers.filter(c => c.name.trim());

      // Flatten all documents with customer reference
      const allDocuments = [];
      validCustomers.forEach(customer => {
        customer.documents.forEach(d => {
          allDocuments.push({
            customer_id: customer.id,
            customer_name: customer.name,
            document_type: d.documentType,
            file_name: d.fileName,
            file_size: d.fileSize
          });
        });
      });

      // Build line_items per applicant from that applicant's own selected services — one line item
      // per (customer, service) carries the applicant identity (customer_name,
      // customer_id_number, customer_emirates_id).
      const lineItems = [];
      let sortOrder = 0;
      validCustomers.forEach((customer) => {
        const custServices = services.filter(s => customer.services.includes(s.id));
        custServices.forEach((svc) => {
          // Distribute the applicant's shared field values to this service's own fields.
          const field_values = (svc.customFields || [])
            .map(f => ({ fieldId: f.fieldId, code: f.code, label: f.label, value: customer.fieldValues?.[f.fieldId] }))
            .filter(fv => fv.value != null && String(fv.value).trim() !== '');
          lineItems.push({
            service_id: svc.id,
            service_name: svc.name,
            service_description: svc.description || '',
            quantity: 1,
            service_charge: svc.service_charge || svc.serviceCharge || 0,
            govt_charge: svc.govt_charge || svc.govtCharge || 0,
            tax_value: svc.tax_on_service_charge || svc.taxOnServiceCharge || 0,
            unit_price: (svc.service_charge || svc.serviceCharge || 0) + (svc.govt_charge || svc.govtCharge || 0),
            is_custom_service: svc.type === 'INTERNAL',
            customer_id: customer.id,
            customer_name: customer.name,
            customer_id_number: customer.passportNumber?.trim() || null,
            customer_emirates_id: customer.emiratesId?.trim() || null,
            field_values,
            sort_order: sortOrder++
          });
        });
      });

      const payload = {
        customer_email: commonEmail.trim() || null,
        customer_phone: commonPhone.trim() || null,
        notes: notes.trim() || null,
        title: title.trim() || null,
        customers: validCustomers.map(c => ({
          id: c.id,
          name: c.name.trim(),
          passport_number: c.passportNumber?.trim() || null,
          emirates_id: c.emiratesId?.trim() || null
        })),
        line_items: lineItems,
        documents: allDocuments
      };

      const response = await ApiService.createPortalWorkOrder(payload);
      const newWO = response?.data?.data;
      const woId = newWO?.id;

      // Upload each document to the WO record (Managers S3). Track any that fail so we can skip the
      // Thaklis sync for them — a document that isn't on the WO must not be pushed to Thaklis.
      const failedDocIds = new Set();
      if (woId) {
        for (const customer of validCustomers) {
          for (const d of (customer.documents || [])) {
            if (!d.file) continue;
            try {
              // Send the label (documentTypeName), not the code
              await ApiService.uploadPortalWorkOrderDocument(woId, d.file, '', d.documentTypeName || d.documentType, customer.name?.trim() || null);
            } catch (uploadErr) {
              console.error('Error uploading document to work order:', uploadErr);
              failedDocIds.add(d.id);
            }
          }
        }
      }

      // Track the applicants + their documents in Thaklis (best-effort — a failure here must not
      // undo the submitted WO), skipping any document whose WO upload failed. Skipped entirely when
      // employee tracking is off (Thaklis disabled, or a typing-center customer).
      if (employeeTrackingActive) {
        try {
          await syncEmployeesAndDocuments(validCustomers, failedDocIds);
        } catch (syncErr) {
          console.error('Employee/document sync failed:', syncErr);
        }
      }

      if (failedDocIds.size > 0) {
        window.alert(`Work order submitted, but ${failedDocIds.size} document(s) failed to upload. Please re-upload them from the work order's Documents section.`);
      }

      setSuccess('Work order submitted successfully! The typing center will review and process it.');

      // Keep the blocking loader up until we navigate away, so the form can't be
      // re-submitted in the gap between the APIs finishing and the redirect.
      setTimeout(() => {
        navigate('/portal/work-orders');
      }, 2000);
    } catch (err) {
      console.error('Failed to create work order:', err);
      setError(err.response?.data?.message || 'Failed to submit work order. Please try again.');
      setLoading(false);
    }
  };

  const currentCustomer = customers[activeTab];
  // Custom fields + required documents merged across the active applicant's selected services.
  const mergedFields = currentCustomer ? mergeCustomFieldsFor(currentCustomer, services) : [];
  const mergedDocs = currentCustomer ? mergeRequiredDocsFor(currentCustomer, services) : [];
  const mergedDocCodes = new Set(mergedDocs.map(d => d.code));
  const uploadedDocCodes = new Set((currentCustomer?.documents || []).map(d => d.documentType));
  const extraDropdownTypes = documentTypes.filter(dt => {
    const code = dt.code || dt.id;
    return !mergedDocCodes.has(code) && !uploadedDocCodes.has(code);
  });
  const extraUploadedDocs = (currentCustomer?.documents || []).filter(d => !mergedDocCodes.has(d.documentType));

  // One uploaded-document row — reused by required-doc slots and the extras list.
  const renderUploadedDoc = (doc) => (
    <ListItem key={doc.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.75, py: 0.5 }}>
      <DocIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{doc.fileName}</Typography>
            <Chip label={doc.documentTypeName} size="small" sx={{ fontSize: '0.65rem' }} />
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          doc.extracting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} />
              <Typography sx={{ fontSize: fontSize.xs, color: 'info.main' }}>Extracting details…</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                {`Size: ${formatFileSize(doc.fileSize)}`}
                {doc.documentNumber ? ` · Doc No: ${doc.documentNumber}` : ''}
              </Typography>
              {(doc.issueDate || doc.expiryDate) && (
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                  {[doc.issueDate ? `Issued ${doc.issueDate}` : null, doc.expiryDate ? `Expires ${doc.expiryDate}` : null].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </>
          )
        }
      />
      <ListItemSecondaryAction>
        <IconButton edge="end" size="small" onClick={() => handleRemoveDocument(doc.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Box sx={PAGE_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => navigate('/portal/work-orders')}
        >
          Back to Work Orders
        </Button>
      </Box>

      {/* Errors surface as a toast so they're seen even when scrolled down the form. */}
      <Snackbar
        open={!!error}
        onClose={(e, reason) => { if (reason !== 'clickaway') setError(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ maxWidth: 480 }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Full-page block while a document is extracting — the form must not be edited until it finishes. */}
      <Backdrop
        open={customers.some(c => (c.documents || []).some(d => d.extracting))}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>Extracting document details… please wait</Typography>
        </Box>
      </Backdrop>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Left Column - Customer Tabs & Services */}
        <Grid item xs={12} md={8}>
          {/* Work order title + common contact information */}
          <Card sx={CARD_SX}>
            <CardContent sx={CONTENT_SX}>
              <Typography sx={SECTION_TITLE_SX}>
                Work Order Details
              </Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5 }}>
                A short title and the contact details for this work order
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Visa renewal – Ahmed & 3 staff"
                    inputProps={{ maxLength: 50 }}
                    helperText={`${title.length}/50`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    type="email"
                    value={commonEmail}
                    onChange={(e) => setCommonEmail(e.target.value)}
                    placeholder="contact@example.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={commonPhone}
                    onChange={(e) => setCommonPhone(e.target.value)}
                    placeholder="+971 50 xxx xxxx"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Customer Tabs Section */}
          <Card sx={CARD_SX}>
            {/* Tab Header */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    py: 0,
                    textTransform: 'none',
                    fontSize: fontSize.sm,
                    fontWeight: 600
                  },
                  '& .Mui-selected': { color: 'primary.main' },
                  '& .MuiTabs-indicator': { backgroundColor: 'primary.main' }
                }}
              >
                {customers.map((customer, index) => (
                  <Tab
                    key={customer.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCustomerComplete(customer) ? (
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 16 }} />
                        )}
                        <span>{customer.name || `Customer ${index + 1}`}</span>
                        {customer.services.length > 0 && (
                          <Chip
                            label={`${customer.services.length} svc`}
                            size="small"
                            sx={{ height: 18, fontSize: '0.625rem', bgcolor: 'action.selected', color: 'primary.main' }}
                          />
                        )}
                        {customer.documents.length > 0 && (
                          <Chip
                            label={`${customer.documents.length} doc`}
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: '0.625rem' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              <Button
                size="small"
                startIcon={<PersonAddIcon fontSize="small" />}
                onClick={handleAddCustomer}
                sx={{ ml: 1, whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>

            {/* Tab Content */}
            {currentCustomer && (
              <CardContent sx={CONTENT_SX}>
                {/* Customer Info */}
                <Typography sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}>
                  Customer Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Full Name"
                      value={currentCustomer.name}
                      onChange={(e) => handleCustomerChange(currentCustomer.id, 'name', e.target.value)}
                      required
                      placeholder="Enter full name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Passport Number"
                      value={currentCustomer.passportNumber}
                      onChange={(e) => handleCustomerChange(currentCustomer.id, 'passportNumber', e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Emirates ID"
                      value={currentCustomer.emiratesId}
                      onChange={(e) => handleCustomerChange(currentCustomer.id, 'emiratesId', e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    {customers.length > 1 && (
                      <Button
                        fullWidth
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => handleRemoveCustomer(currentCustomer.id)}
                        sx={{ height: 40 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      value={currentCustomer.phone}
                      onChange={(e) => handleCustomerChange(currentCustomer.id, 'phone', e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={currentCustomer.email}
                      onChange={(e) => handleCustomerChange(currentCustomer.id, 'email', e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                </Grid>
                {employeeTrackingActive && !currentCustomer.passportNumber?.trim() && !currentCustomer.emiratesId?.trim() && (
                  <Alert severity="warning" sx={{ mt: 1.5, fontSize: fontSize.xs }}>
                    No unique identifier for this applicant. Add a Passport Number or Emirates ID —
                    without one, the employee and their documents can't be tracked, so you won't get
                    expiry reminders for their documents.
                  </Alert>
                )}
              </CardContent>
            )}
          </Card>

          {/* Services Selection — per applicant (the one on the active tab) */}
          <Card sx={CARD_SX}>
            <CardContent sx={CONTENT_SX}>
              <Typography sx={SECTION_TITLE_SX}>
                Select Services
              </Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5 }}>
                Services for <b>{currentCustomer?.name?.trim() || 'this applicant'}</b> — each applicant has their own selection.
              </Typography>

              {loadingServices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Grid container spacing={1}>
                  {services.map((service) => (
                    <Grid item xs={12} sm={6} key={service.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          px: 1, py: 0.25,
                          cursor: 'pointer',
                          bgcolor: (currentCustomer?.services || []).includes(service.id) ? 'action.selected' : 'background.paper',
                          borderColor: (currentCustomer?.services || []).includes(service.id) ? 'primary.main' : 'divider',
                          '&:hover': { borderColor: 'primary.main' }
                        }}
                        onClick={() => handleServiceToggle(service.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(currentCustomer?.services || []).includes(service.id)}
                                onChange={() => handleServiceToggle(service.id)}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: fontSize.sm }}>
                                {service.name}
                                {service.type === 'INTERNAL' && (
                                  <Chip label="Internal" size="small" sx={{ fontSize: '0.6rem', height: 16, ml: 0.75 }} />
                                )}
                                {isB2b && (
                                  <Typography component="span" sx={{ fontSize: fontSize.xs, color: 'text.secondary', ml: 0.75 }}>
                                    · AED {svcTotal(service).toFixed(2)}
                                  </Typography>
                                )}
                              </Typography>
                            }
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Service Information — custom fields merged across the applicant's selected services.
                  A field shared by several services is shown (and entered) once. */}
              {mergedFields.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={SECTION_TITLE_SX}>
                    Service Information
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5 }}>
                    Details needed for this applicant&apos;s services. Shared fields are entered once.
                  </Typography>
                  <Grid container spacing={1.5}>
                    {mergedFields.map((field) => {
                      const val = currentCustomer?.fieldValues?.[field.fieldId] ?? '';
                      const lbl = field.label + (field.required ? ' *' : '');
                      return (
                        <Grid item xs={12} sm={field.fieldType === 'TEXTAREA' ? 12 : 6} key={field.fieldId}>
                          {field.fieldType === 'SELECT' ? (
                            <TextField select fullWidth size="small" label={lbl} value={val}
                              onChange={(e) => handleFieldValueChange(field.fieldId, e.target.value)}>
                              <MenuItem value=""><em>None</em></MenuItem>
                              {(field.options || []).map((opt, i) => (
                                <MenuItem key={i} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                                  {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField fullWidth size="small" label={lbl} value={val}
                              type={field.fieldType === 'NUMBER' ? 'number' : field.fieldType === 'DATE' ? 'date' : 'text'}
                              multiline={field.fieldType === 'TEXTAREA'}
                              rows={field.fieldType === 'TEXTAREA' ? 2 : undefined}
                              InputLabelProps={field.fieldType === 'DATE' ? { shrink: true } : undefined}
                              onChange={(e) => handleFieldValueChange(field.fieldId, e.target.value)} />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}

              {/* Documents — required docs merged across the applicant's selected services (deduped),
                  each with its own upload slot, then an optional "add another". */}
              <Divider sx={{ my: 2 }} />
              <Typography sx={SECTION_TITLE_SX}>
                Documents for {currentCustomer?.name || 'this Customer'}
              </Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5 }}>
                {mergedDocs.length > 0
                  ? 'Attach the documents these services need. Required documents must be uploaded before submitting.'
                  : 'Upload documents specific to this applicant.'}
              </Typography>

              {/* Required / expected document slots (one per document type, deduped) */}
              {mergedDocs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {mergedDocs.map((reqDoc) => {
                    const docsForType = (currentCustomer?.documents || []).filter(d => d.documentType === reqDoc.code);
                    const uploaded = docsForType.length > 0;
                    return (
                      <Paper key={reqDoc.code} variant="outlined"
                        sx={{
                          p: 1.25, mb: 1, borderRadius: 1,
                          borderStyle: uploaded ? 'solid' : 'dashed',
                          borderColor: uploaded ? 'success.main' : (reqDoc.required ? 'warning.main' : 'divider')
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {uploaded
                              ? <CheckIcon sx={{ fontSize: '1.05rem', color: 'success.main' }} />
                              : <DocIcon sx={{ fontSize: '1.05rem', color: reqDoc.required ? 'warning.main' : 'text.disabled' }} />}
                            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>
                              {reqDoc.label}
                              {reqDoc.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                            </Typography>
                          </Box>
                          <Chip
                            label={uploaded ? 'Uploaded' : (reqDoc.required ? 'Required' : 'Optional')}
                            size="small"
                            color={uploaded ? 'success' : (reqDoc.required ? 'warning' : 'default')}
                            sx={{ fontSize: '0.625rem', height: 18 }}
                          />
                        </Box>
                        {uploaded ? (
                          <List dense sx={{ py: 0 }}>
                            {docsForType.map(renderUploadedDoc)}
                          </List>
                        ) : (
                          <Box
                            tabIndex={0}
                            onPaste={(e) => handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label })}
                            onDrop={(e) => { e.preventDefault(); handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label }); }}
                            onDragOver={(e) => e.preventDefault()}
                            sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                              border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                              px: 1.5, py: 0.75, outline: 'none', '&:focus': { borderColor: 'primary.main' }
                            }}>
                            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                              Drag a file here, paste a copied image (Ctrl/⌘+V), or use Upload
                            </Typography>
                            <Button variant="contained" size="small" component="label" startIcon={<UploadIcon fontSize="small" />} sx={{ flexShrink: 0 }}>
                              Upload
                              <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => handleFileUpload(e, { code: reqDoc.code, label: reqDoc.label })} />
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}

              {/* Add another document — types not already covered by a slot or an upload above */}
              {extraDropdownTypes.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                    Add another document
                  </Typography>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Document Type</InputLabel>
                        <Select
                          value={selectedDocType}
                          label="Document Type"
                          onChange={(e) => setSelectedDocType(e.target.value)}
                          disabled={loadingDocTypes}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                        >
                          {(() => {
                            const { primary, secondary } = splitDocTypes(extraDropdownTypes);
                            const item = (dt) => (
                              <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ pl: 3, fontSize: fontSize.sm }}>
                                {dt.label || dt.name || dt.code}
                              </MenuItem>
                            );
                            const out = [];
                            if (primary.length > 0) {
                              out.push(<ListSubheader key="_p" sx={{ bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', color: 'primary.main', lineHeight: '28px' }}>Primary Documents</ListSubheader>);
                              primary.forEach(dt => out.push(item(dt)));
                            }
                            if (secondary.length > 0) {
                              out.push(<ListSubheader key="_s" sx={{ bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', lineHeight: '28px' }}>Secondary Documents</ListSubheader>);
                              secondary.forEach(dt => out.push(item(dt)));
                            }
                            return out;
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        startIcon={<UploadIcon fontSize="small" />}
                        disabled={!selectedDocType}
                      >
                        Upload Document(s)
                        <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} />
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Uploaded documents that don't map to a required slot */}
              {extraUploadedDocs.length > 0 && (
                <List dense>
                  {extraUploadedDocs.map(renderUploadedDoc)}
                </List>
              )}

              {mergedDocs.length === 0 && extraUploadedDocs.length === 0 && extraDropdownTypes.length === 0 && (
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  No documents uploaded yet
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card sx={CARD_SX}>
            <CardContent sx={CONTENT_SX}>
              <Typography sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}>
                Notes / Special Instructions
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions or notes for the typing center..."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent sx={CONTENT_SX}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer' }}
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <Typography sx={SECTION_TITLE_SX}>
                  Order Summary
                </Typography>
                <IconButton size="small">
                  {summaryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Collapse in={summaryExpanded}>
                {/* Customer Summaries */}
                {customers.filter(c => c.name.trim()).map((customer, index) => {
                  const complete = isCustomerComplete(customer);
                  return (
                    <Paper
                      key={customer.id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        mb: 1,
                        borderColor: complete ? 'success.light' : 'divider',
                        bgcolor: activeTab === index ? 'action.hover' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveTab(index)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {complete ? (
                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                            {customer.name}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>
                        {customer.services.length} service{customer.services.length !== 1 ? 's' : ''} · {customer.documents.length} document{customer.documents.length !== 1 ? 's' : ''}
                      </Typography>
                    </Paper>
                  );
                })}

                {customers.filter(c => c.name.trim()).length === 0 && (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No customers added yet
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Totals */}
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Customers</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.filter(c => c.name.trim()).length}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Services</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.reduce((sum, c) => sum + c.services.length, 0)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Documents</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.reduce((sum, c) => sum + c.documents.length, 0)}
                  </Typography>
                </Box>

                {isB2b ? (
                  <Box display="flex" justifyContent="space-between" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>Estimated Total</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: 'primary.main' }}>
                      AED {(
                        customers.reduce((total, c) =>
                          total + services.filter((s) => c.services.includes(s.id)).reduce((sum, s) => sum + svcTotal(s), 0), 0)
                      ).toFixed(2)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>
                    * Pricing will be confirmed by the typing center after review.
                  </Typography>
                )}
              </Collapse>

              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
                onClick={handleSubmit}
                disabled={loading || success}
                sx={{ mt: 2 }}
              >
                {loading ? 'Submitting...' : 'Submit Work Order'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Missing-identifier confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Some applicants have no unique identifier</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            One or more applicants have neither a Passport Number nor an Emirates ID. Those applicants
            won't be tracked, so their documents won't get expiry reminders. You can still submit the
            work order — continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, '& .MuiButton-root': { textTransform: 'none', fontSize: fontSize.sm } }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Go Back
          </Button>
          <Button onClick={proceedSubmit} variant="contained">
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detected applicant differs from the customer on this tab */}
      <Dialog
        open={pendingMismatches.length > 0}
        onClose={handleKeepSameCustomer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Different applicant detected</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            The uploaded document belongs to{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'a different person'}</strong>, but
            this tab is for <strong>{pendingMismatches[0]?.existingName}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: fontSize.sm, mt: 1.5 }}>
            Do you want to keep this document with <strong>{pendingMismatches[0]?.existingName}</strong>,
            or create a new customer for{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'the detected applicant'}</strong> and
            move the document there?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, '& .MuiButton-root': { textTransform: 'none', fontSize: fontSize.sm } }}>
          <Button onClick={handleKeepSameCustomer} color="inherit">
            Keep with {pendingMismatches[0]?.existingName || 'current customer'}
          </Button>
          <Button
            onClick={handleMoveToNewCustomer}
            variant="contained"
          >
            Create new customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocking full-page loader — stays up for the whole submit chain
          (work order → document uploads → Thaklis tracking) until the redirect. */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default PortalCreateWorkOrder;
